/** @format */

import { APP_ID, ErrorHandler, ModuleWithProviders, NgModule } from '@angular/core';
import { BrowserModule, provideClientHydration, withEventReplay } from '@angular/platform-browser';
import { Chain, Environment, LocalStorageConfig } from '@shared/data-access/interfaces';
import {
	CustomUrlSerializer,
	LoggerConfig,
	TaggingService,
	WindowModule
} from '@shared/data-access/services';
import { DCSG_APP_NAME, GlobalErrorHandler, LogDataAccessModule } from '@logger';
import { RouterModule, UrlSerializer } from '@angular/router';

import { APP_BASE_HREF } from '@angular/common';
import { AppComponent } from './app.component';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { CategoryAppRoutingModule } from './app-routing.module';
import { CategoryModule } from './category/category.module';
import { CeopsComponent } from '@shared/ceops';
import { HTTP_INTERCEPTOR_PROVIDERS } from './app.interceptors';
import { SeoService } from '@seo/data-access';
import { chain } from '@core/chain';
import { environment } from '@environment/clp';
import { provideHttpClient } from '@angular/common/http';

const LOCAL_STORAGE_CONFIG: LocalStorageConfig = {
	prefix: ''
};

const LOGGER_CONFIG: LoggerConfig = {
	clientLoggerLevel: environment.logging.client.level,
	xhrLoggerLevel: environment.logging.xhr.level,
	xhrLoggerUrl: `${environment.api.spring.microServiceProxy.logger.path}publish`
};

/*
 * Module Imports
 */
const IMPORTS = [
	BrowserModule,
	BrowserAnimationsModule,
	WindowModule.forRoot(LOCAL_STORAGE_CONFIG),
	LogDataAccessModule.forRoot(LOGGER_CONFIG),
	CeopsComponent,
	CategoryModule
];

/*
 * Module Providers
 */
const PROVIDERS = [
	{ provide: APP_ID, useValue: 'dcsg-ngx-clp-server' },
	{ provide: APP_BASE_HREF, useValue: '/' },
	{ provide: Chain, useValue: chain || {} },
	{ provide: DCSG_APP_NAME, useValue: 'CLP' },
	{ provide: Environment, useValue: environment || {} },
	{
		provide: ErrorHandler,
		useClass: GlobalErrorHandler
	},
	...HTTP_INTERCEPTOR_PROVIDERS,
	{ provide: UrlSerializer, useClass: CustomUrlSerializer },
	SeoService,
	TaggingService,
	provideHttpClient(),
	provideClientHydration(withEventReplay())
];

/**
 * NOTE: This "common" module is used to define the AppComponent since it can only be declared
 * in a single module but is needed in more than one.
 */
@NgModule({
	declarations: [AppComponent],
	imports: [...IMPORTS, RouterModule],
	exports: [AppComponent, RouterModule]
})
export class CategoryCommonAppModule {}

/**
 * NOTE: This is the main product app module and is for the standalone product app and does include the router
 */
@NgModule({
	imports: [...IMPORTS, CategoryAppRoutingModule, CategoryCommonAppModule],
	providers: PROVIDERS,
	bootstrap: [AppComponent]
})
export class CategoryAppModule {}

/**
 * NOTE: This module has the same imports and providers as the "main" standalone product app with the
 * exception to the router module.  The child product app should not have the router included.
 */
@NgModule({
	imports: [...IMPORTS, CategoryCommonAppModule],
	providers: PROVIDERS
})
export class CategoryChildAppModule {}

@NgModule({})
export class CategorySharedAppModule {
	static forRoot(): ModuleWithProviders<CategoryChildAppModule> {
		return {
			ngModule: CategoryChildAppModule,
			providers: PROVIDERS
		};
	}
}
